export class HrRoute<TParamsParser extends ParamsParser, TChildren extends RouteChildren> {
    public pathTemplate: string;
    public children: TChildren;
    private parentRoute: HrRoute<any, any>;
    private paramsParser: TParamsParser;
    private params: Params<TParamsParser>;

    constructor(pathTemplate: string, paramsParser: TParamsParser = null, children: TChildren = null) {
        this.pathTemplate = pathTemplate;
        this.paramsParser = paramsParser;
        this.children = children;
        this.initChildren();
    }

    withParams(value: Params<TParamsParser>): HrRoute<TParamsParser, TChildren> {
        this.params = value;
        return this;
    }


    parseParams(): Params<TParamsParser> {
        const locationParts = window.location.hash.slice(2).split("?");
        const queryParamMap = new URLSearchParams(locationParts[1]);
        return this.parseParamsInternal(locationParts[0].split('/'), queryParamMap);
    }

    protected parseParamsInternal(pathParts: string[], queryParamMap: URLSearchParams): Params<TParamsParser> {
        if (this.parentRoute) {
            this.parentRoute.parseParamsInternal(pathParts, queryParamMap);
        }

        this.params = {} as Params<TParamsParser>;
        Object.entries(this.paramsParser).forEach(([key, parserFunction]) => {
            if (!key.includes('$'))
                return;

            const paramKey = key.replace('$', '');
            const paramValue = queryParamMap.get(paramKey);
            if (paramValue !== null) {
                // @ts-ignore
                this.params[paramKey] = parserFunction(paramValue);
            }
            queryParamMap.delete(paramKey);
        });

        const templateParts = this.pathTemplate.split('/');
        templateParts.forEach(templatePart => {
            if (templatePart.includes(':')) {
                const paramKey = templatePart.replace(':', '');
                if (this.paramsParser[paramKey]) {
                    // @ts-ignore
                    this.params[paramKey] = this.paramsParser[paramKey](pathParts[0]);
                }
                else {
                    // @ts-ignore
                    this.params[paramKey] = null;
                }
            } else if (pathParts[0] !== templatePart) {
                this.params = null;
                console.error('Current route URL does not match with the given HrRoute:' + this.absolutePath);
                throw new Error(`Unable to parse template part ${templatePart}`);
            }
            pathParts.splice(0, 1);
        });

        return this.params;
    }

    get absolutePath(): string {
        return '/' + this.getPathInternal('', []);
    }

    pushToState() {
        const url = this.absolutePath;
        window.history.pushState({}, null,'/#' + url);
    }

    protected getPathInternal(finalPath: string, queryParams: string[]): string {
        let path = this.pathTemplate;
        if (this.params) {
            Object.entries(this.params).forEach(([key, value]) => {
                const templateKey = ':' + key;
                if (path.includes(templateKey))
                    path = path.replace(templateKey, value);
                else if (value !== undefined){
                    if (typeof value == 'object')
                        queryParams.push(new URLSearchParams(key + '=' + JSON.stringify(value)).toString());
                    else
                        queryParams.push(new URLSearchParams(key + '=' + value).toString());
                }
            });
        }

        if (finalPath != '')
            finalPath = path + '/' + finalPath;
        else
            finalPath = path;

        if (this.parentRoute) {
            return this.parentRoute.getPathInternal(finalPath, queryParams);
        }
        return finalPath + (queryParams.length > 0 ? '?' : '') + queryParams.join('&');
    }

    protected setParent(parentRoute: HrRoute<any, any>) {
        this.parentRoute = parentRoute;
    }

    protected initChildren() {
        for (let key in this.children) {
            // @ts-ignore
            this.children[key]?.setParent(this);
        }
    }
}

export function route<TParamsParser extends ParamsParser, TChildren extends RouteChildren>
(pathTemplate: string, paramsParser: TParamsParser = null, children: TChildren = null): HrRoute<TParamsParser, TChildren> {
    return new HrRoute(pathTemplate, paramsParser, children);
}

export const stringParser = (val: string): string => val;
export const numberParser = (val: string): number => isFalsy(val) ? falsify(val) : parseInt(val);
export const booleanParser = (val: string): boolean => isFalsy(val) ? falsify(val) : val?.toLowerCase() == 'true' || val == '1';
export const enumParser = <TEnum>(val: string): TEnum => isFalsy(val) ? falsify(val) : val as TEnum;
export const objectParser = <TObj>(val: string): TObj => isFalsy(val) ? falsify(val) : JSON.parse(val) as TObj;

function isFalsy(val: string): boolean {
    return val == null || val == 'NaN' || val == 'null' || val == 'undefined';
}

function falsify(val: string): any {
    return val == null ? null :
        val == 'NaN' ? NaN :
            val == 'null' ? null :
                val == 'undefined' ? undefined : null;
}


type ParamsParser = {
    [key: string]: (val: string) => any;
}

type RouteChildren = {
    [key: string]: HrRoute<any, any>;
}

type Params<TParamsParser extends ParamsParser> = {
    [Property in keyof TParamsParser as Property extends `${infer P}$` ? never : Property]: TParamsParser[Property] extends (val: string) => any ?
        Property extends `${infer P}$` ? ReturnType<TParamsParser[Property]> : ReturnType<TParamsParser[Property]> : never;
} & {
    [Property in keyof TParamsParser as Property extends `${infer P}$` ? P : never]?: TParamsParser[Property] extends (val: string) => any ?
        Property extends `${infer P}$` ? ReturnType<TParamsParser[Property]> : ReturnType<TParamsParser[Property]> : never;
}
